/**
 * EduCentersR2Service — Geocodificador L1 para centros educativos
 *
 * Busca centros educativos (CEIP, IES, CPR, CEO, EI) de la Junta de Andalucía
 * almacenados en R2 mediante fuzzy matching con uFuzzy.
 *
 * Datos: ~6000+ centros educativos — Junta de Andalucía Datos Abiertos
 * CRS: EPSG:25830 (UTM30 ETRS89, reproyectado desde WGS84)
 * Ruta R2: /edu-centers/{ine5}.json.gz
 * Precisión: ±15 m (datos oficiales Junta)
 * Licencia: CC BY 4.0
 *
 * DIFERENCIA vs OSMSecurityR2Service:
 * Los tokens NO están pre-computados en los datos — se calculan al vuelo
 * normalizando el campo "nombre" (quitar tildes, uppercase, split espacios).
 *
 * @module services/geocoding/specialized
 * @version 1.0.0
 * @date 2026-02-22
 */

import uFuzzy from '@leeoniya/ufuzzy';
import type {
  EduCenterFeature,
  EduCentersSearchOptions,
  EduCentersResult,
} from '../../../types/eduCenters';
import { EDU_X_RANGE, EDU_Y_RANGE, EDU_PRECISION_M } from '../../../types/eduCenters';
import { isSentinelCoord } from '../../../types/eduSentinelCoords';
import { crearUFuzzyConGuarda, type UFuzzyConGuarda } from '../../../lib/fuzzySearch';
import type { GeocodingResult } from '../../../types/infrastructure';
import { fetchWithBackoff } from '../../../utils/fetchWithBackoff';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 */
const WORKER_BASE_URL = WORKER_BASE;
const FETCH_TIMEOUT = 15_000;
const CACHE_MAX_ITEMS = 50;
const BASE_CONFIDENCE = 75;

/** Configuración uFuzzy para nombres españoles de centros educativos */
const UFUZZY_OPTIONS: uFuzzy.Options = {
  intraMode: 1,
  intraIns: 1,
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
  interIns: 6,
};

// =============================================================================
// SINGLETON
// =============================================================================

let serviceInstance: EduCentersR2Service | null = null;

export function getEduCentersR2Service(): EduCentersR2Service {
  if (!serviceInstance) {
    serviceInstance = new EduCentersR2Service();
  }
  return serviceInstance;
}

/** Reset singleton (útil para tests) */
export function resetEduCentersR2Service(): void {
  if (serviceInstance) {
    serviceInstance.clearCache();
  }
  serviceInstance = null;
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class EduCentersR2Service {
  private readonly baseUrl: string;
  private readonly cache = new Map<string, { data: EduCenterFeature[]; ts: number }>();
  private readonly loadingPromises = new Map<string, Promise<EduCenterFeature[]>>();
  private readonly fuzzy: UFuzzyConGuarda;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? WORKER_BASE_URL;
    this.fuzzy = crearUFuzzyConGuarda(UFUZZY_OPTIONS);
  }

  // ---------------------------------------------------------------------------
  // CARGA DE DATOS POR MUNICIPIO
  // ---------------------------------------------------------------------------

  /** Carga features EduCenters de un municipio desde R2 (con cache LRU) */
  async loadMunicipio(codINE: string): Promise<EduCenterFeature[]> {
    if (!/^\d{5}$/.test(codINE)) return [];

    // Cache hit
    const cached = this.cache.get(codINE);
    if (cached) return cached.data;

    // Dedup concurrent requests
    const pending = this.loadingPromises.get(codINE);
    if (pending) return pending;

    const promise = this.fetchMunicipio(codINE);
    this.loadingPromises.set(codINE, promise);

    try {
      const features = await promise;
      this.setCache(codINE, features);
      return features;
    } finally {
      this.loadingPromises.delete(codINE);
    }
  }

  private async fetchMunicipio(codINE: string): Promise<EduCenterFeature[]> {
    const url = `${this.baseUrl}/edu-centers/${codINE}.json.gz`;

    try {
      const { response } = await fetchWithBackoff(url, { timeoutMs: FETCH_TIMEOUT });

      if (response.status === 404) {
        // Municipio sin centros educativos — poco probable pero posible
        return [];
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: EduCenterFeature[] = await response.json();
      const valid = data.filter(
        f => this.isValidCoord(f) && !isSentinelCoord(f.x, f.y),
      );
      const sentinelN = data.filter(
        f => this.isValidCoord(f) && isSentinelCoord(f.x, f.y),
      ).length;
      if (sentinelN > 0) {
        console.warn(
          `[EDU_R2] ${codINE}: filtered ${sentinelN} sentinel centers ` +
            `(see OBS-FT-EDU-R2-COORD-SENTINEL)`,
        );
      }
      return valid;
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.warn(`[EDU_R2] Timeout cargando ${codINE}`);
      } else {
        console.error(`[EDU_R2] Error cargando ${codINE}:`, error);
      }
      return [];
    }
  }

  // ---------------------------------------------------------------------------
  // GEOCODIFICACIÓN
  // ---------------------------------------------------------------------------

  /**
   * Geocodifica un centro educativo buscando en datos R2 de la Junta.
   * Retorna el mejor resultado o null.
   */
  async geocode(options: { codINE: string; name?: string }): Promise<GeocodingResult | null> {
    const results = await this.search({
      codINE: options.codINE,
      name: options.name,
    });
    if (results.length === 0) return null;
    return this.toGeocodingResult(results[0], options.name ?? '');
  }

  /**
   * Busca centros educativos por nombre fuzzy y/o subtipo.
   * Calcula tokens al vuelo normalizando "nombre" (sin tildes, uppercase).
   * Retorna array ordenado por confianza descendente.
   */
  async search(options: EduCentersSearchOptions): Promise<EduCentersResult[]> {
    const { codINE, name, subtipo, minConfidence = 55, maxResults = 5 } = options;

    const features = await this.loadMunicipio(codINE);
    if (features.length === 0) return [];

    // Filtrar por subtipo si especificado
    let candidates = features;
    if (subtipo) {
      candidates = candidates.filter(
        f => f.subtipo.toUpperCase() === subtipo.toUpperCase()
      );
      if (candidates.length === 0) return [];
    }

    // Sin nombre → devolver candidatos filtrados con confianza base
    if (!name?.trim()) {
      return candidates
        .slice(0, maxResults)
        .map(f => this.toResult(f, BASE_CONFIDENCE));
    }

    // Búsqueda fuzzy calculando tokens al vuelo
    return this.fuzzySearch(candidates, name, minConfidence, maxResults);
  }

  // ---------------------------------------------------------------------------
  // FUZZY MATCHING (tokens calculados al vuelo)
  // ---------------------------------------------------------------------------

  private fuzzySearch(
    candidates: EduCenterFeature[],
    searchName: string,
    minConfidence: number,
    maxResults: number,
  ): EduCentersResult[] {
    // Calcular tokens: usar pre-computados si existen, si no, al vuelo
    const haystack = candidates.map(f => f.tokens ?? this.normalizeTokens(f.nombre));
    const normalizedName = this.normalizeTokens(searchName);
    const searchResult = this.fuzzy.search(haystack, normalizedName);

    if (!searchResult?.[0] || searchResult[0].length === 0) return [];

    const [idxs, _info, order] = searchResult;
    const processOrder = order ?? idxs;
    const results: EduCentersResult[] = [];

    for (let i = 0; i < Math.min(processOrder.length, maxResults * 2); i++) {
      const idx = processOrder[i];
      const featureIdx = idxs[idx];
      const feature = candidates[featureIdx];
      if (!feature) continue;

      const tokens = feature.tokens ?? this.normalizeTokens(feature.nombre);
      const fuzzyScore = this.calcFuzzyScore(normalizedName, tokens, feature.nombre);
      const confidence = this.fuzzyScoreToConfidence(fuzzyScore);

      if (confidence >= minConfidence) {
        results.push(this.toResult(feature, confidence, fuzzyScore));
      }
      if (results.length >= maxResults) break;
    }

    return results;
  }

  /**
   * Normaliza nombre a tokens para búsqueda fuzzy.
   * Quita tildes, convierte a lowercase, normaliza espacios.
   */
  normalizeTokens(text: string): string {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')  // Quitar tildes/diacríticos
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Calcula score normalizado 0-1 del fuzzy match */
  private calcFuzzyScore(needle: string, tokens: string, nombre: string): number {
    const nLow = this.normalizeTokens(nombre);
    if (tokens === needle || nLow === needle) return 1.0;
    if (tokens.includes(needle) || nLow.includes(needle)) return 0.95;
    if (needle.includes(tokens) || needle.includes(nLow)) return 0.90;
    const ratio = needle.length / Math.max(tokens.length, needle.length);
    return Math.min(0.85, 0.5 + ratio * 0.4);
  }

  /** Convierte fuzzy score (0-1) a confianza (0-100) — techo 85 por datos oficiales Junta */
  private fuzzyScoreToConfidence(score: number): number {
    if (score >= 0.95) return 85;  // Oficial Junta: techo 85
    if (score >= 0.85) return 80;
    if (score >= 0.70) return 72;
    if (score >= 0.55) return 65;
    if (score >= 0.40) return 58;
    return 50;
  }

  // ---------------------------------------------------------------------------
  // CONVERSIÓN Y VALIDACIÓN
  // ---------------------------------------------------------------------------

  /** Convierte feature a resultado intermedio con confianza */
  private toResult(
    feature: EduCenterFeature,
    confidence: number,
    fuzzyScore?: number,
  ): EduCentersResult {
    return {
      x: feature.x,
      y: feature.y,
      confidence,
      precision: EDU_PRECISION_M,
      source: 'edu_centers_r2',
      feature,
      fuzzyScore,
      matchedName: feature.nombre,
    };
  }

  /** Convierte resultado EduCenters a GeocodingResult estándar */
  toGeocodingResult(
    result: EduCentersResult,
    _searchName: string,
  ): GeocodingResult {
    return {
      x: result.x,
      y: result.y,
      confidence: result.confidence,
      source: 'edu_centers_r2',
      matchedName: result.matchedName,
    };
  }

  /** Valida que las coordenadas estén dentro del rango esperado (Andalucía UTM30) */
  private isValidCoord(feature: EduCenterFeature): boolean {
    return (
      feature.x >= EDU_X_RANGE.min &&
      feature.x <= EDU_X_RANGE.max &&
      feature.y >= EDU_Y_RANGE.min &&
      feature.y <= EDU_Y_RANGE.max
    );
  }

  // ---------------------------------------------------------------------------
  // CACHÉ LRU
  // ---------------------------------------------------------------------------

  /** Inserta en caché con evicción LRU si se excede el límite */
  private setCache(codINE: string, data: EduCenterFeature[]): void {
    if (this.cache.size >= CACHE_MAX_ITEMS) {
      // Evict oldest entry
      let oldestKey: string | null = null;
      let oldestTs = Infinity;
      for (const [key, entry] of this.cache) {
        if (entry.ts < oldestTs) {
          oldestTs = entry.ts;
          oldestKey = key;
        }
      }
      if (oldestKey) this.cache.delete(oldestKey);
    }
    this.cache.set(codINE, { data, ts: Date.now() });
  }

  /** Limpia toda la caché */
  clearCache(): void {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /** Número de entradas en caché (útil para tests) */
  get cacheSize(): number {
    return this.cache.size;
  }

  /** Carga features directamente en caché sin fetch (útil para tests) */
  loadFromData(codINE: string, features: EduCenterFeature[]): void {
    this.setCache(codINE, features);
  }
}
